import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, CardContent, Typography, CircularProgress, Grid } from '@mui/material'
import Alert from '@mui/material/Alert'
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Replay as ResetIcon,
} from '@mui/icons-material'
import { authenticatedFetch } from '../../services/api/apiClient'
import { LoadingSpinner, StatusChip } from '../../components/atoms'
import { BaseTable } from '../../components/TableComponents'
import { apiEndpoints } from '../../consts'
import styles from './SettingsCallImport.module.scss'

const STATUS_STYLES = {
  new: { chipColor: 'success', labelKey: 'importCalls.status.new' },
  replace: { chipColor: 'info', labelKey: 'importCalls.status.replace' },
  duplicate: { chipColor: 'warning', labelKey: 'importCalls.status.duplicate' },
  invalid: { chipColor: 'error', labelKey: 'importCalls.status.invalid' },
}

/**
 * Read a fetch Response as JSON without throwing on non-JSON bodies. When the
 * API server is restarting the CRA dev proxy returns an HTML "Proxy error…"
 * page; parsing that would surface a cryptic "Unexpected token" error, so we
 * return null instead and let callers show a friendly message.
 */
const safeJson = async (response) => {
  try {
    return await response.json()
  } catch {
    return null
  }
}

export default function SettingsCallImport() {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)

  const [step, setStep] = useState('upload')
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [importResult, setImportResult] = useState(null)
  const [error, setError] = useState(null)
  const [dragActive, setDragActive] = useState(false)

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError(t('importCalls.csvFileRequired'))
      return
    }
    setFile(selectedFile)
    setError(null)
    setPreview(null)
    setImportResult(null)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    handleFileSelect(e.dataTransfer.files[0])
  }

  const handleUploadPreview = async () => {
    /* istanbul ignore next -- the upload button is disabled until a file is selected */
    if (!file) return
    setLoading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await authenticatedFetch(apiEndpoints.vendorCallImportPreview, {
        method: 'POST',
        body: formData,
      })
      const data = await safeJson(response)

      if (!response.ok || !data) {
        throw new Error(data?.message || t('importCalls.previewFailed'))
      }

      setPreview(data.data)
      setStep('preview')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmImport = async () => {
    /* istanbul ignore next -- the import button is disabled until a file is selected */
    if (!file) return
    setStep('importing')
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await authenticatedFetch(apiEndpoints.vendorCallImportConfirm, {
        method: 'POST',
        body: formData,
      })
      const data = await safeJson(response)

      if (!response.ok || !data) {
        throw new Error(data?.message || t('importCalls.importFailed'))
      }

      setImportResult(data.data)
      setStep('results')
    } catch (err) {
      setError(err.message)
      setStep('preview')
    }
  }

  const handleReset = () => {
    setStep('upload')
    setFile(null)
    setPreview(null)
    setImportResult(null)
    setError(null)
    /* istanbul ignore next -- ref may be detached when resetting from a non-upload step */
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const previewColumns = [
    { id: 'rowIndex', label: '#' },
    {
      id: 'timestamp',
      label: t('importCalls.table.timestamp'),
      renderCell: (row) => row.timestamp || '-',
    },
    {
      id: 'campaign',
      label: t('importCalls.table.campaign'),
      renderCell: (row) => row.campaign || '-',
    },
    {
      id: 'agentName',
      label: t('importCalls.table.agent'),
      renderCell: (row) => row.agentName || '-',
    },
    {
      id: 'customerName',
      label: t('importCalls.table.customer'),
      renderCell: (row) => row.customerName || '-',
    },
    {
      id: 'disposition',
      label: t('importCalls.table.disposition'),
      renderCell: (row) => row.disposition || '-',
    },
    { id: 'ani', label: t('importCalls.table.ani'), renderCell: (row) => row.ani || '-' },
    {
      id: 'status',
      label: t('importCalls.table.status'),
      renderCell: (row) => {
        const statusStyle = STATUS_STYLES[row.status] || STATUS_STYLES.invalid
        return (
          <StatusChip size="small" label={t(statusStyle.labelKey)} color={statusStyle.chipColor} />
        )
      },
    },
  ]

  const importableCount = preview
    ? preview.newCalls + preview.replaceCalls + preview.existingCalls
    : 0

  return (
    <Box className={styles.root}>
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          style={{ marginBottom: 16, borderRadius: 12 }}
        >
          {error}
        </Alert>
      )}

      {/* Upload Step */}
      {step === 'upload' && (
        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h6" className={styles.cardTitle} gutterBottom>
              {t('importCalls.uploadTitle')}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
              {t('importCalls.uploadDescription')}
            </Typography>

            <Box
              className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault()
                setDragActive(true)
              }}
              onDragLeave={() => setDragActive(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files[0])}
              />
              <UploadIcon
                style={{ fontSize: 48, color: 'var(--color-text-light)', marginBottom: 16 }}
              />
              <Typography
                variant="h6"
                style={{ color: 'var(--color-text-primary)', marginBottom: 8 }}
              >
                {file ? file.name : t('importCalls.dropZoneText')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {file ? `${(file.size / 1024).toFixed(1)} KB` : t('importCalls.dropZoneBrowse')}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="flex-end" style={{ marginTop: 24 }}>
              <Button
                variant="contained"
                onClick={handleUploadPreview}
                disabled={!file || loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />
                }
                className={`${styles.actionButton} ${styles.primaryButton}`}
              >
                {loading ? t('importCalls.processing') : t('importCalls.uploadPreview')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Preview Step */}
      {step === 'preview' && preview && (
        <>
          <Card className={styles.card}>
            <CardContent>
              <Typography variant="h6" className={styles.cardTitle} gutterBottom>
                {t('importCalls.summaryTitle')}
              </Typography>

              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importCalls.totalRows')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}
                    >
                      {preview.totalRows}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importCalls.newCalls')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-success)' }}
                    >
                      {preview.newCalls}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importCalls.replaceCalls')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-info, #0288d1)' }}
                    >
                      {preview.replaceCalls}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importCalls.existingCalls')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-warning)' }}
                    >
                      {preview.existingCalls}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importCalls.skipped')}
                    </Typography>
                    <Typography
                      variant="h5"
                      style={{ fontWeight: 700, color: 'var(--color-text-secondary)' }}
                    >
                      {preview.invalidRows}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box
                style={{
                  padding: 16,
                  backgroundColor: 'var(--color-bg-light)',
                  borderRadius: 12,
                  marginBottom: 16,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importCalls.campaigns')}:{' '}
                      <strong>{preview.campaigns.map((c) => c.name).join(', ') || 'N/A'}</strong>
                    </Typography>
                    {preview.newCampaigns > 0 && (
                      <Typography variant="body2" style={{ color: 'var(--color-success)' }}>
                        {t('importCalls.newCampaigns', { count: preview.newCampaigns })}
                      </Typography>
                    )}
                    <Typography variant="body2" color="textSecondary">
                      {t('importCalls.agents')}:{' '}
                      <strong>{preview.agents.join(', ') || 'N/A'}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      {t('importCalls.dateRange')}:{' '}
                      <strong>
                        {preview.dateRange.earliest && preview.dateRange.latest
                          ? `${preview.dateRange.earliest} to ${preview.dateRange.latest}`
                          : 'N/A'}
                      </strong>
                    </Typography>
                    {preview.duplicateInFile > 0 && (
                      <Typography variant="body2" style={{ color: 'var(--color-warning)' }}>
                        {t('importCalls.duplicateInFile', { count: preview.duplicateInFile })}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              {preview.invalidRows > 0 && (
                <Alert
                  severity="warning"
                  style={{ marginBottom: 8, borderRadius: 12 }}
                  icon={<WarningIcon />}
                >
                  {t('importCalls.invalidWarning', { count: preview.invalidRows })}
                </Alert>
              )}
              {preview.replaceCalls > 0 && (
                <Alert severity="info" style={{ marginBottom: 8, borderRadius: 12 }}>
                  {t('importCalls.replaceNotice', { count: preview.replaceCalls })}
                </Alert>
              )}
              {preview.existingCalls > 0 && (
                <Alert severity="info" style={{ marginBottom: 8, borderRadius: 12 }}>
                  {t('importCalls.updateNotice', { count: preview.existingCalls })}
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card className={styles.card}>
            <CardContent>
              <Typography variant="h6" className={styles.cardTitle} gutterBottom>
                {t('importCalls.previewTitle', { count: preview.previewRows.length })}
              </Typography>

              <BaseTable
                columns={previewColumns}
                data={preview.previewRows}
                tableProps={{ size: 'small' }}
              />

              <Box display="flex" justifyContent="flex-end" style={{ gap: 12, marginTop: 24 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  className={`${styles.actionButton} ${styles.outlinedButton}`}
                >
                  {t('importCalls.cancel')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmImport}
                  disabled={importableCount === 0}
                  className={`${styles.actionButton} ${styles.primaryButton}`}
                >
                  {importableCount === 1
                    ? t('importCalls.importCount', { count: importableCount })
                    : t('importCalls.importCountPlural', { count: importableCount })}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Importing Step */}
      {step === 'importing' && (
        <Card className={styles.card}>
          <CardContent style={{ padding: 48, textAlign: 'center' }}>
            <LoadingSpinner size={48} message={t('importCalls.importing')} />
            <Typography variant="body2" color="textSecondary">
              {t('importCalls.importingDescription')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Results Step */}
      {step === 'results' && importResult && (
        <Card className={styles.card}>
          <CardContent>
            <Box display="flex" alignItems="center" style={{ marginBottom: 24 }}>
              <SuccessIcon
                style={{ fontSize: 32, color: 'var(--color-success)', marginRight: 12 }}
              />
              <Typography variant="h6" className={styles.cardTitle}>
                {t('importCalls.importComplete')}
              </Typography>
            </Box>

            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importCalls.imported')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 700, color: 'var(--color-success)' }}
                  >
                    {importResult.imported}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importCalls.replaced')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 700, color: 'var(--color-info, #0288d1)' }}
                  >
                    {importResult.replaced}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importCalls.updated')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}
                  >
                    {importResult.updated}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importCalls.skipped')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 700, color: 'var(--color-text-secondary)' }}
                  >
                    {importResult.skipped}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box className={styles.statItem}>
                  <Typography variant="body2" color="textSecondary">
                    {t('importCalls.campaignsCreated')}
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}
                  >
                    {importResult.campaignsCreated}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {importResult.errors.length > 0 && (
              <Alert severity="warning" style={{ marginBottom: 16, borderRadius: 12 }}>
                {t('importCalls.errorsWarning', { count: importResult.errors.length })}
                <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                  {importResult.errors.slice(0, 5).map((err) => (
                    <li key={err.rowIndex}>
                      {t('importCalls.table.row')} {err.rowIndex}: {err.error}
                    </li>
                  ))}
                  {importResult.errors.length > 5 && (
                    <li>{t('importCalls.andMore', { count: importResult.errors.length - 5 })}</li>
                  )}
                </ul>
              </Alert>
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleReset}
                startIcon={<ResetIcon />}
                className={`${styles.actionButton} ${styles.primaryButton}`}
              >
                {t('importCalls.uploadAnother')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
